import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxcSweepsClearingV1SweepsTaskQueryServiceSweepsTaskGet } from '@interxfi/api-client/generated/hooks/ixc-sweeps-clearing-v1-sweeps-task-query-service-sweeps-task-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcSweepsClearingV1SweepsTaskState } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { SweepBalancesReconcileOrdersTable } from '../../../../../../../../../../features/sweeps/clearing/task/operation/balances-reconcile/sweep-balances-reconcile-orders-table';
import { SweepBalancesReconcileSegmentsTable } from '../../../../../../../../../../features/sweeps/clearing/task/operation/balances-reconcile/sweep-balances-reconcile-segments-table';
import { SweepsTaskEventDrawer, SweepsTaskEventSummary } from '../../../../../../../../../../features/sweeps/clearing/task/sweeps-task-event';
import { getSweepsTaskStateListItems } from '../../../../../../../../../../features/sweeps/clearing/task/sweeps-task-list-items';
import { getSweepsTaskDetailPath } from '../../../../../../../../../../features/sweeps/clearing/task/sweeps-task-routing';
import { SweepsTaskStatusPill } from '../../../../../../../../../../features/sweeps/clearing/task/sweeps-task-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/sweep/task/_task/$taskId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `sweep-task-${params.taskId}`,
        label: 'Task',
        to: getSweepsTaskDetailPath(params.taskId),
        suffix: <CopyButton content={params.taskId} label="Copy Sweep Task ID" tooltip={{ copy: params.taskId, copied: 'Copied Sweep Task ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Sweep Task'),
  component: SweepTaskDetail,
});

function getListItems(data: IxcSweepsClearingV1SweepsTaskState | undefined) {
  const listItems = getSweepsTaskStateListItems(data);

  return {
    left: [
      listItems.data.operationType,
      ...match(listItems.data.spec)
        .with({ activityReport: P.not(P.nullish) }, ({ activityReport }) => [
          activityReport.value.timestamp,
          activityReport.value.currentCutoff,
          activityReport.value.previousCutoff,
          activityReport.value.symbol,
          activityReport.value.cusip,
          activityReport.value.mapCode,
          activityReport.value.sweepOrderIds,
        ])
        .with({ dividendsPay: P.not(P.nullish) }, ({ dividendsPay }) => [
          dividendsPay.value.endDate,
          dividendsPay.value.details.amountPaid,
          dividendsPay.value.details.executedAt,
          dividendsPay.value.details.transactionId,
        ])
        .with({ dividendsCalculate: P.not(P.nullish) }, ({ dividendsCalculate }) => [
          dividendsCalculate.value.firstDayOfMonth,
          dividendsCalculate.value.dividendDate,
          dividendsCalculate.value.expectedPaymentDate,
          dividendsCalculate.value.dividendRate,
          dividendsCalculate.value.details.dividendChange,
          dividendsCalculate.value.details.sweptBalanceChange,
          dividendsCalculate.value.details.executedAt,
        ])
        .with({ dividendsRecalculate: P.not(P.nullish) }, ({ dividendsRecalculate }) => [
          dividendsRecalculate.value.firstDayOfMonth,
          dividendsRecalculate.value.applyCorrections,
          dividendsRecalculate.value.details.storedDividends,
          dividendsRecalculate.value.details.correctDividends,
          dividendsRecalculate.value.details.dividendsDelta,
          dividendsRecalculate.value.details.storedSweptBalance,
          dividendsRecalculate.value.details.correctSweptBalance,
          dividendsRecalculate.value.details.sweptBalanceDelta,
          dividendsRecalculate.value.details.correctionApplied,
        ])
        .with({ balancesReconcile: P.not(P.nullish) }, ({ balancesReconcile }) => [
          balancesReconcile.value.fromDate,
          balancesReconcile.value.applyCorrections,
          balancesReconcile.value.details.correctionApplied,
          balancesReconcile.value.details.settledHorizonDate,
          balancesReconcile.value.details.paidThroughDate,
          balancesReconcile.value.details.refused,
          balancesReconcile.value.details.expectedAvailableCash,
          balancesReconcile.value.details.storedAvailableCash,
          balancesReconcile.value.details.availableCashDelta,
          balancesReconcile.value.details.settledSweptDelta,
          balancesReconcile.value.details.pendingSweptDelta,
          balancesReconcile.value.details.sweptCashDelta,
          balancesReconcile.value.details.rowsUpdated,
        ])
        .otherwise(() => []),
    ] as const satisfies ReadonlyListItem[],
    right: [
      listItems.metadata.createdAt,
      listItems.sweepsTaskId,
      listItems.sweepsJobId,
      listItems.sweepTargetId,
      listItems.accountId,
      listItems.data.assetId,
      ...match(listItems.data.spec)
        .with({ activityReport: P.not(P.nullish) }, ({ activityReport }) => [activityReport.value.adapterOrderId])
        .otherwise(() => []),
    ] as const satisfies ReadonlyListItem[],
  };
}

function getMenuItems(sweepsTaskId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: SweepsTaskEventDrawer,
        props: { request: { sweepsTaskId } },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load sweep task', 'view this sweep task');

function SweepTaskDetail() {
  const { taskId } = Route.useParams();
  const request = { sweepsTaskId: taskId } as const;
  const { data, error, isPending } = useIxcSweepsClearingV1SweepsTaskQueryServiceSweepsTaskGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.sweepsTask), [data?.sweepsTask]);
  const entityMenuItems = useMemo(() => getMenuItems(taskId), [taskId]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <SweepsTaskStatusPill isPending={showPending} size="large" status={data?.sweepsTask?.status} />
            </HorizontalStack>

            <EntityActionMenu {...entityMenuItems} isPending={showPending} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <SweepsTaskEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>
          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {data?.sweepsTask?.data?.spec && 'balancesReconcile' in data.sweepsTask.data.spec ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Segments</Typography.Level3>
            <SweepBalancesReconcileSegmentsTable segments={data.sweepsTask.data.spec.balancesReconcile.details?.segments ?? STABLE_EMPTY_ARRAY_REF} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {data?.sweepsTask?.data?.spec && 'balancesReconcile' in data.sweepsTask.data.spec ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Orders</Typography.Level3>
            <SweepBalancesReconcileOrdersTable orders={data.sweepsTask.data.spec.balancesReconcile.details?.orders ?? STABLE_EMPTY_ARRAY_REF} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
